import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, CalendarDays } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";

const POSTS = [
  {
    id: "canada-immigration",
    date: "May 2026",
    img: "https://images.unsplash.com/photo-1636829504058-2315a3849412?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0",
    featured: true,
  },
  {
    id: "world-opportunities",
    date: "Apr 2026",
    img: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0",
  },
  {
    id: "uk-skilled-worker",
    date: "Mar 2026",
    img: "https://images.unsplash.com/photo-1549483249-f0b359d1e289?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0",
  },
  {
    id: "passport-tips",
    date: "Feb 2026",
    img: "https://images.unsplash.com/photo-1641736494066-bd18237ede26?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0",
  },
  {
    id: "travel-insurance",
    date: "Jan 2026",
    img: "https://images.unsplash.com/photo-1593182440709-4b7b56482c55?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0",
  },
];

const fade = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] } }),
};

const PostMeta = ({ tag, date }) => (
  <div className="flex items-center gap-3 text-[11px]">
    <span className="rounded-full bg-venka-blue/20 text-venka-green-light px-2.5 py-0.5 font-semibold uppercase tracking-wide">{tag}</span>
    <span className="flex items-center gap-1 text-slate-400"><CalendarDays className="h-3 w-3" /> {date}</span>
  </div>
);

export const BlogPage = () => {
  const { t } = useTranslation();
  const [featured, ...rest] = POSTS;

  return (
    <div className="min-h-screen bg-background text-foreground font-sans flex flex-col">
      <Navbar />
      <main className="flex-1">
        <section className="mx-auto max-w-7xl px-6 pt-16 pb-10" data-testid="blog-hero">
          <p className="text-xs font-semibold tracking-[0.22em] uppercase text-venka-green-light mb-3">{t("blogPage.eyebrow")}</p>
          <h1 className="font-serif text-4xl md:text-5xl text-brand-gradient leading-[1.05]" data-testid="blog-title">
            {t("blogPage.title")}
          </h1>
          <p className="mt-4 text-base text-slate-400 max-w-2xl">
            {t("blogPage.intro")}
          </p>
        </section>

        {/* Featured */}
        <section className="mx-auto max-w-7xl px-6 pb-10" data-testid="blog-featured">
          <div className="group grid lg:grid-cols-2 gap-8 rounded-3xl border border-white/10 bg-card overflow-hidden">
            <div className="relative min-h-[280px] overflow-hidden">
              <img src={featured.img} alt={t(`blogPage.posts.${featured.id}.title`)} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="p-8 lg:p-10 flex flex-col justify-center">
              <PostMeta tag={t(`blogPage.posts.${featured.id}.tag`)} date={featured.date} />
              <h2 className="mt-4 font-serif text-2xl md:text-3xl text-white leading-tight">{t(`blogPage.posts.${featured.id}.title`)}</h2>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">{t(`blogPage.posts.${featured.id}.excerpt`)}</p>
              <span className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-venka-green-light">
                {t("blogPage.readArticle")} <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </span>
            </div>
          </div>
        </section>

        {/* Grid */}
        <section className="mx-auto max-w-7xl px-6 pb-24" data-testid="blog-grid">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((p, i) => (
              <motion.article
                key={p.id}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                variants={fade}
                custom={i}
                className="group rounded-2xl border border-white/10 bg-card overflow-hidden hover:border-venka-blue/50 transition-colors"
                data-testid={`blog-post-${p.id}`}
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={p.img} alt={t(`blogPage.posts.${p.id}.title`)} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <PostMeta tag={t(`blogPage.posts.${p.id}.tag`)} date={p.date} />
                  <h3 className="mt-3 font-serif text-lg text-white leading-snug">{t(`blogPage.posts.${p.id}.title`)}</h3>
                  <p className="mt-2 text-sm text-slate-400 line-clamp-3">{t(`blogPage.posts.${p.id}.excerpt`)}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-venka-green-light">
                    {t("blogPage.readMore")} <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </span>
                </div>
              </motion.article>
            ))}
          </div>
          <div className="mt-12 rounded-3xl border border-white/10 brand-gradient p-8 md:p-10 flex flex-col sm:flex-row items-center justify-between gap-5" data-testid="blog-cta">
            <div>
              <h2 className="font-serif text-2xl text-white">{t("blogPage.ctaTitle")}</h2>
              <p className="text-white/85 text-sm mt-1">{t("blogPage.ctaText")}</p>
            </div>
            <Link to="/apply" className="rounded-full bg-white text-venka-blue hover:bg-white/90 font-semibold px-6 py-3 text-sm shrink-0" data-testid="blog-cta-apply">
              {t("blogPage.ctaButton")}
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPage;
